import React, { useEffect, useState } from "react";
import { api, ApiError, type MaintenanceRequest, type RequestCategory, type RequestComment } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

const CATEGORY_KEY: Record<RequestCategory, string> = {
  plumbing: "requests.categoryPlumbing",
  electrical: "requests.categoryElectrical",
  appliance: "requests.categoryAppliance",
  furniture: "requests.categoryFurniture",
  cleaning: "requests.categoryCleaning",
  pest_control: "requests.categoryPestControl",
  other: "requests.categoryOther",
};

const STATUS_KEY: Record<string, string> = {
  open: "common.statusOpen",
  in_progress: "common.statusInProgress",
  resolved: "common.statusResolved",
};

const NEXT_STATUS: Record<string, "in_progress" | "resolved" | null> = {
  open: "in_progress",
  in_progress: "resolved",
  resolved: null,
};

interface RequestDetailData {
  request: MaintenanceRequest;
  comments: RequestComment[];
  propertyLabel: string | null;
  tenantName: string | null;
  tenantPhone: string | null;
  ownerName: string | null;
  ownerPhone: string | null;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

export function RequestDetail({
  requestId,
  onBack,
  onChanged,
}: {
  requestId: string;
  onBack: () => void;
  onChanged?: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();
  const isOwner = user?.role === "owner";

  const [data, setData] = useState<RequestDetailData | null>(null);
  const [commentBody, setCommentBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  function load() {
    api.get(`/api/requests/${requestId}`).then(setData);
  }
  useEffect(load, [requestId]);

  async function postComment() {
    const body = commentBody.trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post(`/api/requests/${requestId}/comments`, { body });
      setCommentBody("");
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setPosting(false);
    }
  }

  async function updateStatus(status: "in_progress" | "resolved") {
    setUpdatingStatus(true);
    try {
      await api.patch(`/api/requests/${requestId}`, { status });
      load();
      onChanged?.();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setUpdatingStatus(false);
    }
  }

  if (!data) return <div className="spinner" />;
  const { request, comments } = data;
  const otherName = isOwner ? data.tenantName : data.ownerName;
  const otherPhone = isOwner ? data.tenantPhone : data.ownerPhone;
  const next = NEXT_STATUS[request.status];

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("rentalAgreement.back")}
      </button>
      <div className="reg-title">{request.title}</div>
      {data.propertyLabel && <div className="reg-sub">{data.propertyLabel}</div>}

      <div className="card" style={{ marginTop: 4 }}>
        <div className="card-row" style={{ justifyContent: "flex-end" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {request.urgency === "urgent" && <span className="badge pending">{t("requests.urgentBadge")}</span>}
            <span className={`badge ${request.status}`}>{t(STATUS_KEY[request.status] ?? request.status)}</span>
          </div>
        </div>
        {request.category && (
          <div className="chip-row" style={{ marginTop: 10, marginBottom: 0 }}>
            <div className="chip selected">{t(CATEGORY_KEY[request.category])}</div>
          </div>
        )}
        {request.description && <div className="sub" style={{ marginTop: 10 }}>{request.description}</div>}
        {request.photo && (
          <img
            src={request.photo}
            alt=""
            style={{ width: "100%", borderRadius: 11, marginTop: 12, display: "block" }}
          />
        )}

        {isOwner && next && (
          <button className="btn-outline btn-sm" style={{ marginTop: 12 }} disabled={updatingStatus} onClick={() => updateStatus(next)}>
            {next === "in_progress" ? t("ownerRequests.startWork") : t("ownerRequests.markResolved")}
          </button>
        )}

        {otherPhone && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <a className="btn-outline btn-sm" style={{ flex: 1, textAlign: "center", textDecoration: "none" }} href={`tel:${otherPhone}`}>
              {t("requests.callBtn")}
            </a>
            <a
              className="btn-outline btn-sm"
              style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
              href={`https://wa.me/91${otherPhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("requests.whatsappBtn")}
            </a>
          </div>
        )}
      </div>

      <div className="section-title" style={{ marginTop: 22 }}>{t("requests.updatesHeading")}</div>
      {comments.length === 0 && <div className="empty-state">{t("requests.noUpdatesYet")}</div>}
      {comments.map((c) => (
        <div className="card" key={c.id}>
          <div className="card-row">
            <b>{c.authorName}</b>
            <span className="sub">{fmtDateTime(c.createdAt)}</span>
          </div>
          <div className="sub" style={{ marginTop: 6 }}>{c.body}</div>
        </div>
      ))}

      <div className="field" style={{ marginTop: 12 }}>
        <div className="input-wrap"><textarea value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder={t("requests.addCommentPlaceholder")} /></div>
      </div>
      <button className="btn-solid" disabled={posting || !commentBody.trim()} onClick={postComment}>
        {posting ? t("requests.postingCommentBtn") : t("requests.postCommentBtn")}
      </button>
    </>
  );
}
